import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { useDispatch } from 'react-redux';

import { DnaOligo } from 'src/models';
import { STEPS, STEPS_NAMESPACE_ACTION } from 'src/constants/forms';
import withDnaActions from 'src/components/OligoDNA/components/withDnaActions';
import withDnaData from 'src/components/OligoDNA/components/withDnaData';
import withDnaSteps from 'src/components/OligoDNA/components/withDnaSteps';
import withLoader from 'src/core/components/withLoader';
import messages from 'src/components/OligoDNA/components/messages';
import { set } from 'src/core/redux/actions';

import ProgressTabs from './ProgressTabsComponent';

const isFirstTab = (tab) => tab.name === STEPS.UPLOAD_FILE;
const isLastTab = (tab) => tab.name === STEPS.REVIEW_AND_SUBMIT;

const ProgressBarContainer = ({
  addToCart,
  validateData,
  actionErrors,
  clearActionError,
  clearAllActionErrors,
  selectedStep,
  dnaSteps,
  dnaData = [],
  updateSteps,
  showLoader,
  hideLoader,
  getDnaCartActiveStepState,
  dnaType
}) => {
  const [dnaTypeSelected, setDnaTypeSelected] = useState(dnaType);
  const dispatch = useDispatch();
  const canContinue = DnaOligo.isDataValid(dnaData, selectedStep.name);

  // eslint-disable-next-line no-unused-expressions
  useEffect(() => {
    if (dnaType !== dnaTypeSelected) {
      setDnaTypeSelected(dnaType);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dnaType, setDnaTypeSelected]);

  useEffect(() => {
    dispatch(set(STEPS_NAMESPACE_ACTION, '', DnaOligo.getDefaultStepsState()));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dnaTypeSelected]);

  const onClickTab = (targetTab) => {
    if (!targetTab.isVisited || (!canContinue && targetTab.order > selectedStep.order)) {
      return;
    }

    clearAllActionErrors();
    updateSteps(targetTab.name);
  };

  const handleNext = async () => {
    clearAllActionErrors();

    if (isLastTab(selectedStep)) {
      showLoader();

      const isCartRequestValid = await addToCart(dnaData);

      hideLoader();

      if (!isCartRequestValid) {
        return;
      }

      window.location.assign('/store/cart');
    }

    let isValid = true;

    if (selectedStep.name !== STEPS.UPLOAD_FILE) {
      // skip validation only for first tab (file upload) as it's already valdiated
      showLoader();
      isValid = await validateData(dnaData);
    }

    if (isValid) {
      updateSteps(dnaSteps[selectedStep.order + 1].name);
      window.scrollTo(0, 0);
    }

    if (dnaData?.file?.data?.plateSettingsResponse) {
      getDnaCartActiveStepState();
    }

    hideLoader();
  };

  const handlePrevious = () => {
    if (isFirstTab(selectedStep)) {
      return;
    }
    updateSteps(dnaSteps[selectedStep.order - 1].name);
  };

  const onFileDetailsEdit = () => {
    updateSteps(dnaSteps[0].name);
  };

  const onPlateSizeDetailsEdit = () => {
    updateSteps(dnaSteps[1].name);
  };

  const onNormalizationAliquotsDetailsEdit = () => {
    updateSteps(dnaSteps[2].name);
  };

  const onPlatePoolingDetailsEdit = () => {
    updateSteps(dnaSteps[3].name);
  };

  const getContinueBtnTooltipMessage = () => {
    if (selectedStep?.name === dnaSteps[0]?.name) {
      if (dnaData.file?.processed) {
        return messages.tooltipPlateError;
      }

      return messages.tooltipUploadPlateTemplate;
    }

    return messages.tooltipRestSteps;
  };

  return (
    <ProgressTabs
      {...{
        dnaSteps,
        onClickTab,
        onNext: handleNext,
        onPrevious: handlePrevious,
        showFooterPrevious: !isFirstTab(selectedStep),
        showOrderSummaryMobile:
          !isFirstTab(selectedStep) && !isLastTab(selectedStep),
        footerNextMessage: isLastTab(selectedStep) ?
          messages.footerFinish :
          messages.footerNext,
        selectedStep,
        actionErrors,
        clearActionError,
        canContinue,
        onFileDetailsEdit,
        onPlateSizeDetailsEdit,
        onNormalizationAliquotsDetailsEdit,
        onPlatePoolingDetailsEdit,
        getContinueBtnTooltipMessage,
      }}
    />
  );
};

ProgressBarContainer.propTypes = {
  validateData: PropTypes.func.isRequired,
  addToCart: PropTypes.func.isRequired,
  file: PropTypes.object,
  dnaData: PropTypes.object.isRequired,
  dnaSteps: PropTypes.array.isRequired,
  actionErrors: PropTypes.array,
  clearActionError: PropTypes.func.isRequired,
  clearAllActionErrors: PropTypes.func.isRequired,
  updateSteps: PropTypes.func.isRequired,
  selectedStep: PropTypes.object.isRequired,
  showLoader: PropTypes.func.isRequired,
  hideLoader: PropTypes.func.isRequired,
  getDnaCartActiveStepState: PropTypes.func.isRequired,
  dnaType: PropTypes.string
};

export default compose(withDnaActions, withDnaData, withDnaSteps, withLoader)(ProgressBarContainer);
